import { HL } from "../kernel.js";

/**
 * Shelf: a rulebook shelf for ring binders, two tiers of three bays between
 * side panels, under an overhanging top and over a recessed plinth. Each board
 * has a raised front lip with an empty label holder on it, and each bay keeps
 * an L bookend standing on its own, with nothing to hold. The pointer picks a
 * bay: its bookend slides across the bare bay to the wall, and the bookends
 * near it shift less, after it. At rest one bay is bright. The slider is how
 * much of the way across a bookend slides.
 *
 * The pattern: one of many, as Riffle's. A tween per bookend on where it
 * stands, a stagger by bay distance, and a pick on the plane of the shelf's
 * front, which never moves.
 */
const {
  Cam, clamp, facing, fillet, fit, poly, prism, proj, rings, rrect,
  tdone, tset, tval, tween, disposer, mk, pointer, put, register, solid,
} = HL;

const T = 2.4, BT = 2.4, BK = 1.6, BW = 44, TIER = 36, D = 24, Z0 = 6, LT = 1.8, LIP = 3.6, OV = 1.6; // panel, board, back, bay width, clear height, depth, plinth, lip depth, lip rise, top overhang
const COLS = 3, L = (COLS + 1) * T + COLS * BW, ZT = Z0 + 2 * (BT + TIER), S = 1.85;
const FOOT = 9, TK = 1.1, BH = 24, Y0 = 5, Y1 = 19, SPAN = 26, STEP = 45;                            // a bookend's foot, plate thickness, height, depth, how far from the wall it may stand
const REST = [0.4, 0.6, 0.2, 0.86, 0.32, 0.72], MARK = 2, FALL = [1, 0.65, 0.35, 0.12];             // rest spots by bay number, the bay lit at rest, the falloff by bay distance
/** A bookend's upright plate in its own (y, z) plane: square at the foot, its front corner well rounded, where the eye sees it. */
const PLATE = fillet([[Y0, 0], [Y1, 0], [Y1, BH], [Y0, BH]], [0.5, 0.5, 7, 3], 4);

function mount({ stage, svg, read }, value) {
  const bag = disposer();
  const C = Cam(45, 0.5, S);
  fit(C, [[-OV, -OV, 0], [L + OV, -OV, 0], [-OV, D + OV, 0], [L + OV, D + OV, 0], [-OV, -OV, ZT + BT], [L + OV, D + OV, ZT + BT]], 200, 166);
  const P = proj(C), front = facing(C);
  const g = mk("g", {}, svg);
  const block = (x0, y0, x1, y1, r, b, z0, z1) => { const [o, i] = rings(x0, y0, x1, y1, r, b); const s = solid(g); put(s, prism(P, front, o, i, z0, z1)); return s; };
  const onY = (ring, y) => poly(ring.map((q) => P(q.u, y, q.v)));

  // Back to front: the left panel, the back, the plinth's kick; then each tier from the bottom up,
  // its board, its bays left to right with the divider after each, its lip and holders; the right panel; the top.
  block(0, 0, T, D, 1, 0.6, 0, ZT);
  block(T, 0, L - T, BK, 0.6, 0.4, Z0, ZT);
  block(T, D - 5, L - T, D - 3.4, 0.5, 0.3, 0, Z0);

  const bays = [];
  for (let k = 0; k < 2; k++) {
    const zb = Z0 + k * (BT + TIER), zt = zb + BT;
    block(T, BK, L - T, D - LT, 0.8, 0.8, zb, zt);
    for (let c = 0; c < COLS; c++) {
      const n = (1 - k) * COLS + c + 1, xa = T + c * (BW + T);
      const bg = mk("g", {}, g);
      const back = mk("path", { class: "lo" }, bg), face = mk("path", { class: "sil" }, bg), base = mk("path", { class: "sil" }, bg);
      bays.push({ n, k, c, xa, zt, base, back, face, tw: tween(REST[n - 1]), drawn: NaN });
      if (c < COLS - 1) block(xa + BW, BK, xa + BW + T, D - LT, 0.8, 0.6, zt, zt + TIER);
    }
    block(T, D - LT, L - T, D, 0.6, 0.5, zb, zt + LIP);
    for (const b of bays.filter((b) => b.k === k)) {
      const xm = b.xa + BW / 2;
      b.holder = mk("path", { class: "nf", d: onY(rrect(xm - 7, zb + 0.9, xm + 7, zt + LIP - 0.9, 1.3, 3), D) }, g);
      mk("path", { class: "nf lo", d: onY(rrect(xm - 5.6, zb + 2, xm + 5.6, zt + LIP - 2, 0.6, 2), D) }, g);
    }
  }
  block(L - T, 0, L, D, 1, 0.6, 0, ZT);
  block(-OV, -OV, L + OV, D + OV, 2, 1.2, ZT, ZT + BT);

  /** Bay b's bookend, its plate a share u of SPAN from the left wall; its foot points right, where the binders would stand. */
  function draw(b, u) {
    if (u === b.drawn) return;
    b.drawn = u;
    const xp = b.xa + 1.2 + u * SPAN, at = (x) => poly(PLATE.map(([y, z]) => P(x, y, b.zt + z)));
    b.base.setAttribute("d", prism(P, front, rrect(xp, Y0, xp + TK + FOOT, Y1, 0.6, 2), null, b.zt, b.zt + 0.8).sil);
    b.back.setAttribute("d", at(xp));
    b.face.setAttribute("d", at(xp + TK));
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const b of bays) { draw(b, tval(b.tw, now)); if (!tdone(b.tw, now)) moving = true; }
    return moving;
  });
  bag.add(B.unregister);

  /** The world point (x, z) on the shelf's front plane, y = D, under a screen point. */
  const o = P(0, D, 0), ax = P(1, D, 0), az = P(0, D, 1);
  const ex = [ax[0] - o[0], ax[1] - o[1]], ez = [az[0] - o[0], az[1] - o[1]], det = ex[0] * ez[1] - ex[1] * ez[0];
  function onFront(p) {
    const qx = p[0] - o[0], qy = p[1] - o[1];
    return [(qx * ez[1] - qy * ez[0]) / det, (ex[0] * qy - ex[1] * qx) / det];
  }
  /** The bay whose opening, lip and holder hold the pointer, on the front where it always stands; null off the shelf. */
  function pick(p) {
    const [x, z] = onFront(p);
    if (x < 0 || x > L || z < Z0 || z > ZT + BT) return null;
    const k = z < Z0 + BT + TIER ? 0 : 1, c = clamp(Math.floor((x - T / 2) / (BW + T)), 0, COLS - 1);
    return bays.find((b) => b.k === k && b.c === c);
  }

  let reach = value, act = null;
  /** Picks bay a (null puts the shelf back to rest). The stagger spreads out from the bay picked, or the one let go. */
  function choose(a, force) {
    if (a === act && !force) return;
    const now = performance.now(), from = a || act;
    act = a;
    for (const b of bays) {
      const d = from ? Math.abs(b.c - from.c) + Math.abs(b.k - from.k) : 0;
      const u = REST[b.n - 1] * (a ? 1 - reach * FALL[Math.min(d, 3)] : 1);
      tset(b.tw, u, now, d * STEP);
      const lit = a ? b === a : b.n === MARK;
      b.face.classList.toggle("hi", lit);
      b.holder.classList.toggle("hi", lit);
    }
    read.textContent = a ? `bay ${a.n} · 0` : "rest";
    B.wake();
  }
  choose(null, true);
  for (const b of bays) draw(b, REST[b.n - 1]);

  bag.add(pointer(stage, { move: (p) => choose(pick(p)), leave: () => choose(null) }));
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => { reach = v; choose(act, true); },
    destroy: bag.dispose,
  };
}

export default {
  name: "shelf",
  means: "A binder shelf with every bay bare: the bay under the pointer slides its bookend across, with nothing to hold.",
  rules: [1, 2, 5, 9],
  range: [0.4, 0.85, 1],
  tour: [[124, 108], [245, 230], null],
  mount,
};
